/**
 * Save state schema (v1) and high-level accessors.
 *
 * Everything is one JSON blob in localStorage. Reads pull the whole thing,
 * fill in any missing fields with sensible defaults, and return a fully-formed
 * object. Writes go through commit() which serializes the whole thing back.
 *
 * Why one blob?
 *   - The data is tiny (a few KB at most for years of dailies).
 *   - One read at game start, one write per significant event.
 *   - Easier to version and migrate than fragmented per-feature keys.
 *
 * The shape is forward-compatible: adding fields with defaults means older
 * saves load cleanly. If the schema ever needs a breaking change, bump SCHEMA_VERSION
 * and add a migration step in load().
 */

import { readJson, writeJson } from './storage';

const SCHEMA_VERSION = 1;

export interface DailyEntry {
  /** UTC date key, YYYY-MM-DD. Duplicated here for convenience (also the map key). */
  date: string;
  /** Score from the player's first attempt that day. */
  firstAttemptScore: number;
  /** Whether the first attempt collected all coconuts. */
  firstAttemptComplete: boolean;
  /** Best score across all attempts that day (>= firstAttemptScore). */
  bestScore: number;
  /** Total attempts made that day. */
  attempts: number;
  /** Wall-clock ISO timestamp of first attempt completion (win or death). */
  firstAttemptAtISO: string;
}

export interface AdventureBest {
  /** Best total score across a full Adventure run. 0 if never played. */
  bestTotal: number;
  /** Highest level reached, 1..7. 0 if never played. */
  highestLevel: number;
  /** ISO timestamp of when bestTotal was achieved. */
  achievedAtISO: string | null;
}

export interface Settings {
  /** Whether sound effects play. Defaults to true. */
  soundEnabled?: boolean;
  /**
   * Touch control scheme: 'swipe' (default) reads flick direction from the
   * finger's down-point; 'quadrant' reads finger position vs screen center.
   */
  touchMode?: 'swipe' | 'quadrant';
  /** Whether haptic feedback (Vibration API) fires. Defaults to true. */
  hapticsEnabled?: boolean;
}

export interface SaveState {
  version: number;
  daily: Record<string, DailyEntry>;
  adventureBest: AdventureBest;
  settings: Settings;
}

const DEFAULT_STATE: SaveState = {
  version: SCHEMA_VERSION,
  daily: {},
  adventureBest: { bestTotal: 0, highestLevel: 0, achievedAtISO: null },
  settings: {},
};

// --- Load / save ------------------------------------------------------------

export function load(): SaveState {
  const raw = readJson<Partial<SaveState>>({});
  // Merge with defaults so missing fields don't blow up downstream.
  return {
    version: SCHEMA_VERSION,
    daily: raw.daily ?? {},
    adventureBest: {
      ...DEFAULT_STATE.adventureBest,
      ...(raw.adventureBest ?? {}),
    },
    settings: raw.settings ?? {},
  };
}

export function commit(state: SaveState): boolean {
  return writeJson(state);
}

// --- Daily accessors --------------------------------------------------------

export function todaysSeedString(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getDailyEntry(state: SaveState, dateKey: string): DailyEntry | null {
  return state.daily[dateKey] ?? null;
}

/**
 * Record a completed daily attempt (win or death). Returns the updated state
 * AND whether this was the first attempt of the day.
 */
export function recordDailyAttempt(
  state: SaveState,
  dateKey: string,
  score: number,
  complete: boolean
): { state: SaveState; isFirstAttempt: boolean } {
  const existing = state.daily[dateKey];
  const nowISO = new Date().toISOString();

  if (!existing) {
    const entry: DailyEntry = {
      date: dateKey,
      firstAttemptScore: score,
      firstAttemptComplete: complete,
      bestScore: score,
      attempts: 1,
      firstAttemptAtISO: nowISO,
    };
    const newState: SaveState = {
      ...state,
      daily: { ...state.daily, [dateKey]: entry },
    };
    return { state: newState, isFirstAttempt: true };
  }

  // Replay: only update best score + attempts count.
  const entry: DailyEntry = {
    ...existing,
    bestScore: Math.max(existing.bestScore, score),
    attempts: existing.attempts + 1,
  };
  const newState: SaveState = {
    ...state,
    daily: { ...state.daily, [dateKey]: entry },
  };
  return { state: newState, isFirstAttempt: false };
}

// --- Streak (derived on demand) --------------------------------------------

/**
 * Current daily streak: consecutive UTC days ending today (or yesterday, if
 * today hasn't been played yet) where the first attempt was a completed win.
 *
 * The "or yesterday" tolerance means the streak stays alive on the day before
 * you've played the new daily — otherwise a streak would always be 0 first
 * thing in the morning until you played.
 */
export function computeStreak(state: SaveState, today: Date = new Date()): number {
  // Determine the starting day for the walk.
  const todayKey = todaysSeedString(today);
  const todayEntry = state.daily[todayKey];
  const startToday = todayEntry?.firstAttemptComplete === true;

  if (!startToday) {
    // If today wasn't completed yet, check yesterday — keep the streak alive
    // until end of today.
    const yesterday = new Date(today);
    yesterday.setUTCDate(today.getUTCDate() - 1);
    const yKey = todaysSeedString(yesterday);
    if (!state.daily[yKey]?.firstAttemptComplete) return 0;
    return walkStreak(state, yesterday);
  }
  return walkStreak(state, today);
}

function walkStreak(state: SaveState, startDay: Date): number {
  let count = 0;
  const cursor = new Date(startDay);
  while (true) {
    const key = todaysSeedString(cursor);
    const entry = state.daily[key];
    if (!entry?.firstAttemptComplete) break;
    count++;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return count;
}

// --- Adventure --------------------------------------------------------------

/**
 * Record an Adventure run result. Updates bestTotal/highestLevel if exceeded.
 * Always increments attempts implicitly via the carryScore semantics in
 * GameScene; here we just track personal best.
 */
export function recordAdventureRun(
  state: SaveState,
  finalTotal: number,
  highestLevel: number
): SaveState {
  const prev = state.adventureBest;
  if (finalTotal <= prev.bestTotal && highestLevel <= prev.highestLevel) {
    return state;
  }
  const next: AdventureBest = {
    bestTotal: Math.max(prev.bestTotal, finalTotal),
    highestLevel: Math.max(prev.highestLevel, highestLevel),
    achievedAtISO:
      finalTotal > prev.bestTotal ? new Date().toISOString() : prev.achievedAtISO,
  };
  return { ...state, adventureBest: next };
}
